import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@project/translate';
import { DateFormatter, DEFAULT_DATE_FORMAT, LibValidators, TGuid } from '@core/helpers';
import {
  EDoctorSpecializationDTO,
  EGenderDTO,
  getDoctorSpecializationLocalization,
  getSelectableDoctorSpecializationCodes,
  IDoctorToInvite,
  License,
} from '@project/view-models';

export interface IDoctorInviteFormModel {
  firstName: string;
  lastName: string;
  email: string;
  gender: EGenderDTO;
  specialization: EDoctorSpecializationDTO;
  licenses?: License[];
  phone?: string;
  dateOfBirth?: string;
  tag?: TGuid;
  cpf: string;
  cns?: string;
}

export class DoctorInviteUtils {
  // Bulk import only: accepts the active configured specializations (Firestore/cache), or the local enum
  // values when there is no remote list. The manual invite form only requires a value.
  private static specializationValidator() {
    return LibValidators.oneOfValues(
      () => getSelectableDoctorSpecializationCodes(),
      TranslateService.localize('validations.not-allowed'),
    );
  }

  static getPrivateHcDoctorInviteFromDeclaration(fb: FormBuilder) {
    return {
      email: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.email(TranslateService.localize('validations.invalid-email')),
        ],
      ],
      gender: [
        null,
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.enumValue(EGenderDTO, TranslateService.localize('validations.not-allowed')),
        ],
      ],
      firstName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      lastName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      specialization: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      dateOfBirth: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.date(DEFAULT_DATE_FORMAT, TranslateService.localize('validations.invalid')),
        ],
      ],
      phone: [],
      licenses: fb.array([
        fb.group({
          state_id: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
          license: [
            '',
            [
              LibValidators.required(TranslateService.localize('validations.required')),
              LibValidators.numberOnly(TranslateService.localize('validations.should-be-integer')),
            ],
          ],
        }),
      ]),
      cpf: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.cpf(TranslateService.localize('validations.invalid')),
        ],
      ],
      tag: [],
    };
  }

  static getPublicHcDoctorInviteFromDeclaration(fb: FormBuilder) {
    return {
      email: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.email(TranslateService.localize('validations.invalid-email')),
        ],
      ],
      gender: [
        null,
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.enumValue(EGenderDTO, TranslateService.localize('validations.not-allowed')),
        ],
      ],
      firstName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      lastName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      specialization: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      dateOfBirth: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.date(DEFAULT_DATE_FORMAT, TranslateService.localize('validations.invalid')),
        ],
      ],
      phone: [],
      licenses: fb.array([
        fb.group({
          state_id: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
          license: [
            '',
            [
              LibValidators.required(TranslateService.localize('validations.required')),
              LibValidators.numberOnly(TranslateService.localize('validations.should-be-integer')),
            ],
          ],
        }),
      ]),
      cpf: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.cpf(TranslateService.localize('validations.invalid')),
        ],
      ],
      cns: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.cns(TranslateService.localize('validations.invalid')),
        ],
      ],
      tag: [],
    };
  }

  static getPublicHcDoctorInviteFromDeclarationImport(fb: FormBuilder) {
    return {
      email: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.email(TranslateService.localize('validations.invalid-email')),
        ],
      ],
      gender: [
        null,
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.enumValue(EGenderDTO, TranslateService.localize('validations.not-allowed')),
        ],
      ],
      firstName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      lastName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      specialization: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          DoctorInviteUtils.specializationValidator(),
        ],
      ],
      dateOfBirth: ['', [LibValidators.date(DEFAULT_DATE_FORMAT, TranslateService.localize('validations.invalid'))]],
      phone: [],
      cpf: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.cpf(TranslateService.localize('validations.invalid')),
        ],
      ],
      cns: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.cns(TranslateService.localize('validations.invalid')),
        ],
      ],
    };
  }

  static getPrivateHcDoctorInviteFromDeclarationImport(fb: FormBuilder) {
    return {
      email: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.email(TranslateService.localize('validations.invalid-email')),
        ],
      ],
      gender: [
        null,
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.enumValue(EGenderDTO, TranslateService.localize('validations.not-allowed')),
        ],
      ],
      firstName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      lastName: ['', [LibValidators.required(TranslateService.localize('validations.required'))]],
      specialization: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          DoctorInviteUtils.specializationValidator(),
        ],
      ],
      dateOfBirth: ['', [LibValidators.date(DEFAULT_DATE_FORMAT, TranslateService.localize('validations.invalid'))]],
      phone: [],
      cpf: [
        '',
        [
          LibValidators.required(TranslateService.localize('validations.required')),
          LibValidators.cpf(TranslateService.localize('validations.invalid')),
        ],
      ],
    };
  }

  static createDoctorInviteDto(formData: IDoctorInviteFormModel): IDoctorToInvite {
    return {
      email: formData.email ? formData.email.trim() : null,
      first_name: formData.firstName.trim(),
      last_name: formData.lastName.trim(),
      gender: formData.gender,
      phone: formData.phone ? formData.phone.trim().replace(/[)(-]/g, '') : null,
      date_birth: formData.dateOfBirth
        ? DateFormatter.stringToDate(formData.dateOfBirth.trim().replace(/[/-]/g, '.'), { format: DEFAULT_DATE_FORMAT })
        : null,
      specialization: formData.specialization || null,
      specializationLabel: formData.specialization
        ? getDoctorSpecializationLocalization(formData.specialization)
        : null,
      licenses: formData.licenses ? formData.licenses : null,
      tag: formData.tag,
      cpf: formData.cpf.trim(),
      cns: formData.cns?.trim(),
    };
  }
}
